import { useEffect, useState } from "react";
import { Plus, Trash2, Shield, UserCog } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const ROLE_COLORS = {
  super_admin: "bg-[#ff0033]/15 text-[#ff5577] border-[#ff0033]/40",
  admin: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  operatore: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  dipendente: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  cliente: "bg-white/10 text-white/60 border-white/20",
};

export default function UsersPage() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", password: "", role: "operatore", telefono: "" });

  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Utente creato");
      setShow(false);
      setForm({ nome: "", cognome: "", email: "", password: "", role: "operatore", telefono: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare utente?")) return;
    try { await api.delete(`/users/${id}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const changeRole = async (id, role) => {
    try { await api.put(`/users/${id}/role?role=${role}`); load(); toast.success("Ruolo aggiornato"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Solo super_admin"); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Utenti & Ruoli</h1>
          <p className="text-sm text-white/45 mt-1">{list.length} utenti · super_admin / admin / operatore / dipendente / cliente</p>
        </div>
        <button data-testid="user-add" onClick={() => setShow(true)} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
          <Plus size={14} /> Nuovo Utente
        </button>
      </div>

      <div className="glass rounded-2xl overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left p-4">Utente</th>
              <th className="text-left p-4">Email · Telefono</th>
              <th className="p-4">Ruolo</th>
              <th className="p-4">Creato</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.map((u) => (
              <tr key={u.id} className="hover:bg-white/[0.02]" data-testid={`user-${u.email}`}>
                <td className="p-4">
                  <div className="font-medium">{u.nome} {u.cognome}</div>
                  <div className="text-xs text-white/40 font-mono">{u.id?.slice(0, 8)}</div>
                </td>
                <td className="p-4">
                  <div className="text-xs">{u.email}</div>
                  <div className="text-xs text-white/40">{u.telefono || '—'}</div>
                </td>
                <td className="p-4 text-center">
                  <select value={u.role} onChange={(e) => changeRole(u.id, e.target.value)} disabled={user?.role !== 'super_admin'}
                    className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${ROLE_COLORS[u.role]} bg-transparent disabled:opacity-100 disabled:cursor-default`}>
                    {["super_admin", "admin", "operatore", "dipendente", "cliente"].map((r) => <option key={r} className="bg-[#0a0a0a]">{r}</option>)}
                  </select>
                </td>
                <td className="p-4 text-center font-mono text-[10px] text-white/40">{u.created_at?.slice(0, 10)}</td>
                <td className="p-4 text-right">
                  {u.id !== user?.id && user?.role === 'super_admin' && (
                    <button onClick={() => remove(u.id)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-[#ff0033]/15 text-white/60 hover:text-[#ff0033]">
                      <Trash2 size={13} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {user?.role !== 'super_admin' && (
        <div className="glass rounded-xl p-4 text-xs text-white/55 flex items-center gap-3">
          <Shield size={14} className="text-amber-300" />
          Solo gli utenti con ruolo <b className="text-[#ff5577]">super_admin</b> possono modificare ruoli ed eliminare altri utenti.
        </div>
      )}

      {show && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-md glass-strong rounded-3xl p-6" data-testid="user-form">
            <div className="font-display text-2xl font-bold mb-5">Nuovo Utente</div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="Nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                <input required placeholder="Cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })}
                  className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              </div>
              <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <input placeholder="Telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <input required type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 text-sm">
                {["operatore", "dipendente", "admin", "super_admin"].map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button data-testid="user-form-submit" type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">Crea</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
